<template>
  <svg
    version="1.2"
    xmlns="http://www.w3.org/2000/svg"
    xmlns:xlink="http://www.w3.org/1999/xlink"
    class="graph"
    aria-labelledby="title"
    role="img"
  >
    <title v-if="chartTitle" id="title">{{ chartTitle }}</title>
    <g class="grid x-grid" id="xGrid">
      <line v-for="p in yPoints" :x1="point(origin.x)" :y1="point(p.y)" :x2="point(xMax)" :y2="point(p.y)" v-bind:key="p"></line>
    </g>
    <g class="labels x-labels">
      <text v-for="p in xPoints" :x="point(p.x)" :y="point(p.y)" v-bind:key="p">{{ p.value }}</text>
      <text :x="point(xLabel.x)" :y="point(xLabel.y)" class="label-title">{{ xLabel.label }}</text>
    </g>
    <g class="grid y-grid" id="yGrid">
      <line v-for="p in xPoints" :x1="point(p.x)" :y1="point(origin.y)" :x2="point(p.x)" :y2="point(yMax)" v-bind:key="p"></line>
    </g>
    <g class="labels y-labels">
      <text v-for="p in yPoints" :x="point(p.x)" :y="point(p.y)" v-bind:key="p">{{ p.value }}</text>
      <text :x="point(yLabel.x)" :y="point(yLabel.y)" class="label-title">{{ yLabel.label }}</text>
    </g>
    <g class="data">
      <circle v-for="p in points" :cx="point(p.x)" :cy="point(p.y)" r="4" v-bind:key="p"></circle>
    </g>
    <g class="link">
      <line v-for="l in lines" :x1="point(l.start.x)" :y1="point(l.start.y)" :x2="point(l.end.x)" :y2="point(l.end.y)" v-bind:key="l"></line>
    </g>
    <g class="dataS2">
      <circle v-for="p in pointsS2" :cx="point(p.x)" :cy="point(p.y)" r="4" v-bind:key="p"></circle>
    </g>
    <g class="linkS2">
      <line v-for="l2 in linesS2" :x1="point(l2.start.x)" :y1="point(l2.start.y)" :x2="point(l2.end.x)" :y2="point(l2.end.y)" v-bind:key="l2"></line>
    </g>
  </svg>
</template>

<script>
import Vue from "vue"

export default Vue.extend({
  props: {
    chartTitle: {
      type: String,
      required: false
    },
    labels: {
      type: Object,
      required: false,
      default() {
        return {
          x: this.$t('Material.Chart.axis-x-label'),
          y: this.$t('Material.Chart.axis-y-label', {um: this.timeUm})
        }
      }
    },
    x: {
      type: Object,
      required: false,
      default() {
        return {
          min: 160,
          max: 210,
          step: 10
        }
      }
    },
    y: {
      type: Object,
      required: false,
      default() {
        return {
          min: 0,
          max: 30,
          step: 5
        }
      }
    },
    values: {
      type: Array,
      required: true
    },
    valuesS2: {
      type: Array,
      required: false
    },
    timeUm: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      origin: {
        x: 15,
        y: 85
      },
      xMax: 95,
      yMax: 5
    }
  },
  methods: {
    point(value) {
      return value + "%"
    },
    pointsArray(min, max, step) {
      const values = []

      for (let i = min; i <= max; i += step) {
        values.push(i)
      }

      return values
    },
    value2Point(value) {
      return {
        x: this.calculateX(value.x),
        y: this.calculateY(value.y)
      }
    },
    calculateX(x) {
      return this.scaleDown(x, this.origin.x, this.xMax, this.xNormalized.min, this.xNormalized.max)
    },
    calculateY(y) {
      return this.scaleDown(y, this.origin.y, this.yMax, this.yNormalized.min, this.yNormalized.max)
    },
    scaleDown(x, a, b, min, max) {
      return (b - a) * (x - min) / (max - min) + a
    },
    normalizeAxis(axis) {
      const steps = (axis.max - axis.min) / axis.step;

      if(Number.isInteger(steps)) return axis

      const normFloor = axis.min + (Math.floor(steps) * axis.step)
      const normCeil = axis.min + (Math.ceil(steps) * axis.step)

      console.warn('Incoherent values: provided max is ' + axis.max + ' but for the given step [' + axis.step + '] should be ' + normFloor + ' or ' + normCeil + '. Using: ' + normCeil + '.')

      return {
        min: axis.min,
        max: normCeil,
        step: axis.step
      }
    }
  },
  computed: {
    hasTitle() {
      return !!this.chartTitle
    },
    hasXLabel() {
      return !!this.labels.x
    },
    xLabel() {
      if(this.hasXLabel) {
        return {
          x: (this.xMax - this.origin.x) * 0.5,
          y: this.origin.y + (100 - this.origin.y) * 4/5,
          label: this.labels.x
        }
      }
    },
    xNormalized() {
      return this.normalizeAxis(this.x)
    },
    xPoints() {
      return this.pointsArray(this.xNormalized.min, this.xNormalized.max, this.xNormalized.step).map(
        (item, i, full) => {
          return {
            x: this.origin.x + (i * (this.xMax - this.origin.x)) / (full.length - 1),
            y: this.origin.y + (100 - this.origin.y) * 2/5,
            value: item
          }
        }
      )
    },
    hasYLabel() {
      return !!this.labels.y
    },
    yLabel() {
      if(this.hasYLabel) {
        return {
          x: this.origin.x * 2/7,
          y: (this.origin.y - this.yMax) * 0.5,
          label: this.labels.y
        }
      }
    },
    yNormalized() {
      return this.normalizeAxis(this.y)
    },
    yPoints() {
      return this.pointsArray(this.yNormalized.min, this.yNormalized.max, this.yNormalized.step).map(
        (item, i, full) => {
          return {
            x: this.origin.x * 5/7,
            y: this.origin.y - (i * (this.origin.y - this.yMax)) / (full.length - 1),
            value: item
          }
        }
      )
    },
    points() {
      return this.values.map(this.value2Point)
    },
    pointsS2() {
      return this.valuesS2 ? this.valuesS2.map(this.value2Point) : []
    },
    lines() {
      const lines = []

      for(let i = 0; i < this.points.length - 1; i++) {
        lines.push({
          start: this.points[i],
          end: this.points[i + 1]
        })
      }

      return lines
    },
    linesS2() {
      const linesS2 = []

      for(let i = 0; i < this.pointsS2.length - 1; i++) {
        linesS2.push({
          start: this.pointsS2[i],
          end: this.pointsS2[i + 1]
        })
      }

      return linesS2
    },
  }
})
</script>

<style>
.graph .labels.x-labels {
  text-anchor: middle;
}

.graph .labels.y-labels {
  text-anchor: middle;
}

.graph .labels.y-labels text {
  dominant-baseline: central;
}

.graph .labels.y-labels .label-title {
  writing-mode: vertical-rl;
}

.graph {
  width: 100%;
  min-height: 300px;
  border: 2px solid black;
  padding: 2%;
}

.graph .grid {
  stroke: #ccc;
  stroke-dasharray: 0;
  stroke-width: 1;
}

.labels {
  font-size: 100%;
}

.label-title {
  font-weight: bold;
  text-transform: capitalize;
  font-size: 90%;
  fill: black;
}

.data {
  fill: blue;
  stroke-width: 1;
}

.link {
  stroke: blue;
}


.dataS2 {
  fill: red;
  stroke-width: 1;
}

.linkS2 {
  stroke: red;
}
</style>
